import React from 'react';
import {
  LINEAGE, PLATFORM_CRATES, SIGNALS, STAGES, WORKBENCH, YOCHO_CRATES, YOCHO_STAGES,
} from './data';

const hue = (n) => ({ '--hue': `var(--kz-h${n})` });

const JUMPS = [['#signal', 'Signals'], ['#how', 'Pipeline'], ['#signals', 'Example signals'], ['#lineage', 'Lineage'],
  ['#workbench', 'Workbench'], ['#architecture', 'Architecture'], ['#api', 'API']];

export default function Engineers() {
  return (
    <>
      <section className="kz-eng-band" id="engineers">
        <div className="kz-container">
          <p className="kz-kicker kz-k3">For your engineers</p>
          <h2 className="kz-h2">How it is built.</h2>
          <p className="kz-lead">The detail your architects and security reviewers will ask for: the signal model, the pipeline, lineage, the workbench, the crates and the API.</p>
          <nav className="kz-jumps" aria-label="Engineering sections">
            {JUMPS.map(([h, t]) => <a key={h} href={h}>{t}</a>)}
          </nav>
        </div>
      </section>

      <section className="wx-section kz-section" id="signal">
        <div className="kz-container kz-split">
          <div>
            <p className="kz-kicker kz-k5">The idea</p>
            <h2 className="kz-h2">A signal is the unit of everything.</h2>
            <p className="kz-p">
              <i>Kizashi</i> means a sign or omen, an early indication that something is about to happen. The platform takes
              the traffic a business already has and turns it into signals: small, typed measurements about an entity at a
              moment, each registered with its kind, unit, producer, rollup rule and retention tier.
            </p>
            <p className="kz-p">
              Adding a signal is a registry entry plus an extractor, with no schema migration. Signals are never updated or
              discarded; a delete, a move or a read is itself an event. Full resolution stays hot, then rolls up hourly and
              daily, and any signal that drove an alert or a score change is pinned so its evidence survives.
            </p>
            <p className="kz-p kz-muted">
              Value kinds: <code className="wx-code-inline">gauge</code>, <code className="wx-code-inline">counter</code>, <code className="wx-code-inline">categorical</code>, <code className="wx-code-inline">score</code>.
            </p>
          </div>
          <pre className="wx-code kz-code" aria-label="The shape of a signal row"><code>
            <span className="wx-tok-comment">{'// one narrow, append-only ClickHouse table'}</span>{'\n'}
            {'Signal {\n'}
            {'  '}<span className="wx-tok-keyword">entity_type</span>{'        "customer"\n'}
            {'  '}<span className="wx-tok-keyword">entity_id</span>{'          "cust-0007"\n'}
            {'  '}<span className="wx-tok-keyword">signal_type</span>{'        "email.time_to_first_response"\n'}
            {'  '}<span className="wx-tok-keyword">ts</span>{'                 2026-08-14T09:00:00Z\n'}
            {'  '}<span className="wx-tok-keyword">value</span>{'              gauge 31.5  '}<span className="wx-tok-comment">{'// business hours'}</span>{'\n'}
            {'  '}<span className="wx-tok-keyword">dims</span>{'               { bu: "industrial" }\n'}
            {'  '}<span className="wx-tok-keyword">extractor_version</span>{'  "reply-tracker@…"\n'}
            {'  '}<span className="wx-tok-keyword">provenance</span>{'         '}<span className="wx-tok-string">{'<encrypted, per-subject key>'}</span>{'\n'}
            {'}'}
          </code></pre>
        </div>
      </section>

      <section className="wx-section wx-section--alt kz-section" id="how">
        <div className="kz-container">
          <p className="kz-kicker kz-k6">How it works</p>
          <h2 className="kz-h2">Ingest, normalise, analyse, trigger, act.</h2>
          <p className="kz-lead">
            Five decoupled stages joined by a RabbitMQ bus. Any stage can be scaled, retried or replaced on its own, and raw
            data is kept under policy so logic can improve and be replayed without going back to the source.
          </p>
          <ol className="kz-flow">
            {STAGES.map((s) => (
              <li key={s.n} className="kz-flow-step" style={hue(s.hue)}>
                <div className="kz-flow-top">
                  <span className="kz-flow-n">{s.n}</span>
                  <span className="kz-icon-tile kz-icon-sm"><i className={s.icon} aria-hidden="true" /></span>
                </div>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
                <code className="kz-flow-msg">{s.msg}</code>
              </li>
            ))}
          </ol>
          <div className="kz-yocho">
            <div className="kz-yocho-head">
              <span className="kz-yocho-name" lang="ja">予兆</span>
              <div>
                <h3>Yochō, the signal layer</h3>
                <p>Added after normalisation: generic signals, entity resolution, detectors and scorers that every pack reuses. Nothing downstream of extraction sees raw content.</p>
              </div>
            </div>
            <div className="kz-yocho-grid">
              {YOCHO_STAGES.map(([t, d], i) => (
                <div key={t} className="kz-yocho-step">
                  <span className="kz-yocho-i">{String.fromCharCode(65 + i)}</span>
                  <h4>{t}</h4>
                  <p>{d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="wx-section kz-section" id="signals">
        <div className="kz-container">
          <p className="kz-kicker kz-k2">Example pack: customer health and churn early warning</p>
          <h2 className="kz-h2">The leading indicators are broken promises, worsening tone and slow replies.</h2>
          <p className="kz-lead">
            Churn means a customer stops ordering. The order stop is the label, the answer key; the job is to warn well
            before it. These are the signals the pack produces today, by source.
          </p>
          <div className="kz-table-wrap">
            <table className="kz-table">
              <thead><tr><th scope="col">Source</th><th scope="col">How it arrives</th><th scope="col">Signals</th></tr></thead>
              <tbody>
                {SIGNALS.map((r) => (
                  <tr key={r.s} style={hue(r.hue)}><th scope="row"><span className="kz-src-dot" aria-hidden="true" />{r.s}</th><td>{r.how}</td><td>{r.sig}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="kz-note">
            Cross-source: the same issue raised by email, then a ticket, is one issue escalating, not two. A ticket that
            started as an email is deduplicated against its thread.
          </p>
        </div>
      </section>

      <section className="wx-section wx-section--alt kz-section" id="lineage">
        <div className="kz-container kz-split kz-split-rev">
          <div>
            <p className="kz-kicker kz-k5">Explainability</p>
            <h2 className="kz-h2">Every score shows its working.</h2>
            <p className="kz-p">
              In the customer-health pack, a health score breaks down into per-input contributions, each contribution into
              the signals that fed it, each signal into its evidence: the threads, tickets, orders and invoices it came from.
              The unanswered-email chain can still be drawn after the raw buffer is purged, because thread skeletons keep
              message ids, times, direction and reply state, with encrypted participants and no bodies.
            </p>
            <p className="kz-p">
              Every signal carries a ULID, its source item ids and the extractor, model and config versions that made it,
              so any number can be reproduced as the system knew it at the time.
            </p>
          </div>
          <ol className="kz-lineage" aria-label="Lineage from score to raw content">
            {LINEAGE.map(([t, d], i) => (
              <li key={t} style={{ '--i': i, '--hue': `var(--kz-h${[2, 1, 5, 6, 3][i]})` }}>
                <b>{t}</b><span>{d}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="wx-section kz-section" id="workbench">
        <div className="kz-container">
          <p className="kz-kicker kz-k4">Workbench</p>
          <h2 className="kz-h2">Analysts tune and test by pointing and clicking.</h2>
          <p className="kz-lead">Nobody hand-writes rules. The UI produces structured definitions, and every change is versioned, previewed and audited.</p>
          <div className="kz-cards">
            {WORKBENCH.map((c) => (
              <div className="kz-card" key={c.title} style={hue(c.hue)}>
                <span className="kz-icon-tile"><i className={c.icon} aria-hidden="true" /></span>
                <h3>{c.title}</h3>
                <p>{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="wx-section wx-section--alt kz-section" id="architecture">
        <div className="kz-container">
          <p className="kz-kicker kz-k6">Architecture</p>
          <h2 className="kz-h2">One Rust workspace, one crate per service.</h2>
          <div className="kz-stack">
            {[['Bus', 'RabbitMQ', 8], ['Hot, config, audit', 'Postgres', 6], ['Signals and aggregates', 'ClickHouse', 2],
              ['Archive', 'S3 or Azure Blob', 7], ['Models', 'Local inference; Azure AI Foundry', 5], ['Deploy', 'Compose → Container Apps → Helm', 1]].map(([b, v, h]) => (
                <div key={b} style={hue(h)}><b>{b}</b><span>{v}</span></div>
            ))}
          </div>
          <div className="kz-crates">
            <div>
              <h3>Platform <span>{PLATFORM_CRATES.length} crates</span></h3>
              <ul>{PLATFORM_CRATES.map((c) => <li key={c}><code>{c}</code></li>)}</ul>
            </div>
            <div>
              <h3>Yochō <span>{YOCHO_CRATES.length} crates</span></h3>
              <ul>{YOCHO_CRATES.map((c) => <li key={c}><code>{c}</code></li>)}</ul>
            </div>
          </div>
          <p className="kz-note">
            Engineering rules the repository holds itself to: tests first, tests in their own files, no file over 500 lines,
            an ADR for every architectural decision, and an append-only feature log entry in the same change. Every message
            published on the bus gets a contract test.
          </p>
        </div>
      </section>

      <section className="wx-section kz-section" id="api">
        <div className="kz-container kz-split">
          <div>
            <p className="kz-kicker kz-k1">Public API, customer-health pack</p>
            <h2 className="kz-h2">Customers at critical risk, in one call.</h2>
            <p className="kz-p">
              A service account’s key resolves the tenant, the role and the business-unit scope; none of them come from the
              request. Risk bands of the 0–100 score: healthy under 20, low under 40, moderate under 60, high under 80,
              critical from 80.
            </p>
            <p className="kz-p kz-muted">Also: one customer’s contributions and trajectory, a signal’s time series, alerts with snooze and acknowledge, cases, and golden-set downloads for admins.</p>
          </div>
          <pre className="wx-code kz-code"><code>
            <span className="wx-tok-comment">$</span>{' curl -H "Authorization: Bearer $KEY" \\\n    "$BASE/api/v1/yocho/customers?band=critical&limit=20"\n\n'}
            {'{"data": [{"id": "…", "name": "Acme",\n'}
            {'           "health": {"score": 86.4, "band": "critical",\n'}
            {'                      "as_of": "2026-09-26T00:00:00Z"},\n'}
            {'           "alert_active": true, "open_chains": 3}],\n'}
            {' "next_cursor": null}'}
          </code></pre>
        </div>
      </section>
    </>
  );
}
